import { atom, PrimitiveAtom, useSetAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { DeliveryMethods } from "../utils/enum";
import { objectIsEmpty } from "../utils/helpers";
import { editOrderAtom } from "./index";
import { posOrderDeliveryMethodAtom } from "./usePosOrderDeliveryMethodAtom";
import { posOrderTableMethodAtom } from "./usePosOrderTable";
import { posShowPaymentAtom } from "./usePosShowPayment";
import { posOrderTipAtom } from "./usePosOrderTipAtom";

export interface PosOrderSessionData {
  session_id?: number;
  total_amount?: number;
  order_ids?: Record<string, number>;
  current_table_ids?: number[];
}

export const posOrderSessionDataAtom = atom(
  null
) as PrimitiveAtom<PosOrderSessionData | null>;

export const useSetPosOrderSessionData = () =>
  useSetAtom(posOrderSessionDataAtom);

export const posOrderSessionIdAtom = selectAtom(
  posOrderSessionDataAtom,
  (data) => data?.session_id
);

export const posOrderSessionTotalAmountAtom = selectAtom(
  posOrderSessionDataAtom,
  (data) => data?.total_amount ?? 0
);

export const posOrderSessionOrderIdsAtom = selectAtom(
  posOrderSessionDataAtom,
  (data) => data?.order_ids ?? {}
);

export const posOrderSessionCurrentTableIdsAtom = selectAtom(
  posOrderSessionDataAtom,
  (data) => data?.current_table_ids ?? []
);

const isTableMethodAtom = atom(
  (get) => get(posOrderDeliveryMethodAtom) === DeliveryMethods.DINE_IN
);

const isOrderEditingAtom = atom(
  (get) => objectIsEmpty(get(editOrderAtom)) === false
);

export const hasSessionAtom = atom((get) => {
  const table = get(posOrderTableMethodAtom);
  const sessionId = get(posOrderSessionDataAtom)?.session_id;
  const isTableMethod = get(isTableMethodAtom);
  return table !== null && sessionId !== undefined && isTableMethod;
});

export const hasOrdersAtom = atom((get) => {
  const orderIds = get(posOrderSessionOrderIdsAtom);
  return Object.keys(orderIds).length > 0;
});

export const showPaymentsAtom = atom((get) => {
  const isShowPayments = get(posShowPaymentAtom);
  const isOrderEditing = get(isOrderEditingAtom);
  return isShowPayments && !isOrderEditing;
});

const tipValueAtom = atom((get) => {
  const tip = get(posOrderTipAtom);
  return parseFloat(tip === "-1" || !tip ? "0" : tip);
});

export const sessionTotalAmountAtom = atom((get) => {
  const totalAmount = get(posOrderSessionTotalAmountAtom);
  const tipValue = get(tipValueAtom);
  return totalAmount + tipValue;
});

export const isTableAndNoSessionAtom = atom((get) => {
  const isTableMethod = get(isTableMethodAtom);
  const sessionId = get(posOrderSessionDataAtom)?.session_id;
  return isTableMethod && !sessionId;
});
export const isTableAndHasSessionAtom = atom((get) => {
  const isTableMethod = get(isTableMethodAtom);
  const sessionId = get(posOrderSessionDataAtom)?.session_id;
  return isTableMethod && sessionId > 0;
});

export const isTableOrderEditingAtom = atom((get) => {
  const isTableMethod = get(isTableMethodAtom);
  const isOrderEditing = get(isOrderEditingAtom);
  return isTableMethod && isOrderEditing;
});
